import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { ApolloError } from 'apollo-client';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import type { WorkspaceV2Launcher } from 'bundles/labs-common/types/LabLaunch';
import { getWorkspaceV2Launcher } from 'bundles/labs-common/utils/WorkspaceApiUtils';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getIsLockedForSubmitting } from 'bundles/learner-progress/utils/Item';
import { ProgrammingSubmissionSchemaTypeNames } from 'bundles/programming/components/__types__/ProgrammingSubmissionSchemaTypes';
import { useLazyProgrammingAssignmentQuery } from 'bundles/programming/hooks/useProgrammingAssignment';
import type { LearnerAssignment } from 'bundles/programming/hooks/useProgrammingAssignment';
import submissionSummariesStore from 'bundles/programming/stores/submissionSummariesStore';
import { isSleuthAssignment } from 'bundles/programming/utils/SleuthUtils';

export type LearnerProgrammingAssignmentContextType = {
  assignment: LearnerAssignment | undefined;
  computedItem: Item;
  courseId: string;
  courseSlug: string;
  fetchLabLauncher: () => Promise<void>;
  fetchLearnerProgrammingAssignmentData: () => Promise<void>;
  isLabLauncherLoading: boolean;
  isLabSubmission: boolean;
  isLoading: boolean;
  isProgrammingAssignmentLoading: boolean;
  itemId: string;
  itemMetadata: ItemMetadata;
  labLauncher: WorkspaceV2Launcher | null;
  labLauncherLoadingError?: Error;
  learnerProgrammingAssignmentLoadingError?: ApolloError;
};

export const LearnerProgrammingAssignmentContext = createContext<LearnerProgrammingAssignmentContextType | undefined>(
  undefined
);

export type PropsToProvider = {
  // These props are used; this lint is misfiring on this multi-prop file
  /* eslint-disable react/no-unused-prop-types */
  computedItem: Item;
  courseId: string;
  courseSlug: string;
  itemMetadata: ItemMetadata;
  /* eslint-enable react/no-unused-prop-types */
};

/**
 * Internal hook for all provider value calculation. Do not import.
 * @private
 */
export const useLearnerProgrammingAssignmentContextValue = (
  props: PropsToProvider
): LearnerProgrammingAssignmentContextType => {
  const { computedItem, courseId, courseSlug, itemMetadata } = props;
  const {
    id: itemId,
    contentSummary: { typeName: itemTypeName },
  } = computedItem;

  const [labLauncher, setLabLauncher] = useState<WorkspaceV2Launcher | null>(null);
  const [isLabLauncherLoading, setIsLabLauncherLoading] = useState(false);
  const [labLauncherLoadingError, setLabLauncherLoadingError] = useState<Error | undefined>(undefined);
  const router = useRouter();

  const isModernProgramming = itemTypeName === 'gradedProgramming' || itemTypeName === 'ungradedProgramming';

  const [
    loadProgrammingAssignment,
    { assignment, error: loadProgrammingAssignmentError, loading: isProgrammingAssignmentLoading },
  ] = useLazyProgrammingAssignmentQuery(courseId, itemId);
  const submissionSchemaTypename = assignment?.submissionSchema?.__typename;
  const isSleuth = isSleuthAssignment(courseId, itemId);
  const isLabSubmission =
    submissionSchemaTypename === ProgrammingSubmissionSchemaTypeNames.LAB || isSleuth || !!labLauncher;

  const fetchLearnerProgrammingAssignmentData = useCallback<() => Promise<void>>(async () => {
    await loadProgrammingAssignment();

    // Loaded but not used directly - load data for the MetaInfoArea line
    if (isModernProgramming) {
      await submissionSummariesStore.load({
        userId: user.get().id,
        courseId,
        courseSlug,
        itemId,
        itemTypeName,
      });
    }
  }, [courseId, courseSlug, isModernProgramming, itemId, itemTypeName, loadProgrammingAssignment]);

  const fetchLabLauncher = useCallback<() => Promise<void>>(async () => {
    if (
      isLabSubmission &&
      // Don't fetch the workspace launcher for LOCKED_FOR_SUBMITTING items, because it would reject anyways.
      !getIsLockedForSubmitting(computedItem) &&
      // Don't re-fetch the lab launcher if we have one already
      !labLauncher
    ) {
      const programId = router.location?.query?.programId ?? undefined;
      setIsLabLauncherLoading(true);
      setLabLauncherLoadingError(undefined);
      try {
        const result = await getWorkspaceV2Launcher(courseId, itemId, programId);
        setLabLauncher(result);
      } catch (error) {
        setLabLauncherLoadingError(error);
      } finally {
        setIsLabLauncherLoading(false);
      }
    }
  }, [computedItem, courseId, isLabSubmission, itemId, labLauncher, router]);

  useEffect(() => {
    fetchLearnerProgrammingAssignmentData();
  }, [fetchLearnerProgrammingAssignmentData]);

  useEffect(() => {
    fetchLabLauncher();
  }, [fetchLabLauncher]);

  return {
    assignment,
    computedItem,
    courseId,
    courseSlug,
    fetchLabLauncher,
    fetchLearnerProgrammingAssignmentData,
    isLabLauncherLoading,
    isLabSubmission,
    isLoading: isProgrammingAssignmentLoading || isLabLauncherLoading,
    isProgrammingAssignmentLoading,
    itemId,
    itemMetadata,
    labLauncher,
    labLauncherLoadingError,
    learnerProgrammingAssignmentLoadingError: loadProgrammingAssignmentError,
  };
};

export const LearnerProgrammingAssignmentContextProvider = (props: React.PropsWithChildren<PropsToProvider>) => {
  const { children } = props;
  const contextValue = useLearnerProgrammingAssignmentContextValue(props);

  return (
    <LearnerProgrammingAssignmentContext.Provider value={contextValue}>
      {children}
    </LearnerProgrammingAssignmentContext.Provider>
  );
};

export const useLearnerProgrammingAssignmentContext = () => {
  const context = useContext(LearnerProgrammingAssignmentContext);
  if (context === undefined) {
    throw new Error(
      'useLearnerProgrammingAssignmentContext must be used within a LearnerProgrammingAssignmentContextProvider'
    );
  }
  return context;
};
